import React, {useCallback} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {GiftshopPackageActivesResponse, IconType} from 'types'
import {convertToSmallestTimeUnit} from 'utils'
import {Icon, Image, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {ThemeColor} from 'themes'
import {useSelector} from 'lib/redux'

interface StyledPictureProps {
  padded?: boolean
}

const StyledPicture = styled(Image)<StyledPictureProps>`
  object-fit: contain;
  height: ${convertUnit(48)};
  border-radius: ${convertUnit(2.5)};
  aspect-ratio: 0.75;
  border: 1px solid ${({theme}) => theme.white_1};
  margin-top: ${({padded}) => (padded ? convertUnit(10) : 0)};
`

const StyledPackageContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(18)};
  padding: ${convertUnit(9)} ${convertUnit(20)};
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
`

const StyledPackagePictureContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledPackageDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledPackageActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: ${convertUnit(4)};
`

const StyledPackageAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export default function GiftShopPackageActiveItem({
  package: packageData,
  onClickPackage,
}: {
  package: GiftshopPackageActivesResponse
  onClickPackage: () => void
}) {
  const {translate} = useTranslation()
  const user = useSelector('user')!!

  const {tree_name, creator_username, tree_photo_url, expired_at} = packageData

  const handleRenderPackageAction = useCallback(
    (icon: IconType, color: ThemeColor, text: string, onClick?: () => void) => (
      <StyledPackageAction
        onClick={(e) => {
          if (onClick) {
            e.stopPropagation()
            onClick()
          }
        }}>
        <Icon size={12} color={color} type={icon} />
        <Paragraph color={color}>{text}</Paragraph>
      </StyledPackageAction>
    ),
    [],
  )

  const getTimeLeft = useCallback(
    (expiredAt: string) => {
      const now = new Date()
      const {units, value} = convertToSmallestTimeUnit(now, expiredAt)

      switch (units) {
        case 'days':
          return `${value} ${translate('global:days', {
            count: value,
          })}`.toLowerCase()
        case 'hours':
          return `${value} ${translate('global:hour', {
            count: value,
          })}`.toLowerCase()
        case 'minutes':
          return `${value} ${translate('global:minute', {
            count: value,
          })}`.toLowerCase()
        default:
          return translate(
            'giftShop:packageDetailActiveTimeLeftLessThanAMin',
          ).toLowerCase()
      }
    },
    [translate],
  )

  return (
    <StyledPackageContainer onClick={onClickPackage}>
      <StyledPackagePictureContainer>
        <StyledPicture src={user.photo} alt="Creator Photo" />
        <StyledPicture padded src={tree_photo_url} alt="Tree Photo" />
      </StyledPackagePictureContainer>
      <StyledPackageDetailContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('giftShop:packageDetailTitle', {
            creator: creator_username,
          })}
        </Paragraph>
        <Paragraph color="gray_4">
          {translate('giftShop:packageActiveItemTree', {
            tree: tree_name,
          })}
        </Paragraph>
        <StyledPackageActionContainer>
          {expired_at &&
            handleRenderPackageAction(
              'clock-ol',
              'warning_5',
              translate('giftShop:packageDetailActiveTimeLeft', {
                time: getTimeLeft(expired_at),
              }),
            )}
        </StyledPackageActionContainer>
      </StyledPackageDetailContainer>
    </StyledPackageContainer>
  )
}
